import { initializeKeyHierarchyQuery } from '@polaris/core/keys'
import type { Account, SessionKeyData } from '@polaris/core/models'
import type { QueryClient } from '@tanstack/react-query'
import { makeAuthWindowClient } from './auth/window-client'
import type { CreateAccountParams, User } from './user'

// These are special functions that are authed and can be used in a window context with access token.
// Currently useful for login flow.

const getUser = (accessToken: string): Promise<User> =>
  makeAuthWindowClient(accessToken).get('user').json<User>()

export async function commitAccounts(
  accounts: Account[],
  accessToken: string,
  sessionKeyData?: SessionKeyData,
  queryClient?: QueryClient,
) {
  const user = await getUser(accessToken)

  const apiAccounts = accounts.map<CreateAccountParams>((account) => ({
    id: account.id!,
    user_id: user.id,
    name: account.name,
    provider_name: account.provider_info.name,
    provider_logo: account.provider_info.img,
    type: account.type,
    address: account.address,
    ecosystem: account.ecosystem,
    grouping_key: account.grouping_key,
    status: account.account_status,
  }))

  await makeAuthWindowClient(accessToken).post('user/accounts/batch', {
    json: {
      accounts: apiAccounts,
    },
  })

  // Initialize key hierarchy for MPC users (if sessionKeyData is provided)
  // This pre-creates the key hierarchy (root key, runner secret, Namada key)
  if (sessionKeyData) {
    try {
      if (queryClient) {
        // Use React Query cache when queryClient is available
        await queryClient.ensureQueryData(
          initializeKeyHierarchyQuery({
            sessionKeyData,
          }),
        )
        console.log('Key hierarchy initialized (cached)')
      } else {
        // Fallback to direct API call when queryClient is not available (shouldn't happen in normal flow)
        const response = await makeAuthWindowClient(accessToken)
          .post('mpc/initialize-key-hierarchy', {
            json: {
              sessionKeyData,
            },
          })
          .json()
        console.log('Key hierarchy initialized (direct)', response)
      }
    } catch (error) {
      // Log warning since this is non-critical with automatic fallback
      // The key hierarchy will be created inline on first privacy swap if this fails
      console.warn(
        'Key hierarchy initialization failed (non-critical - fallbacks to initializing on first privacy swap):',
        error instanceof Error ? error.message : error,
      )
    }
  }
}
